import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE || '').replace(/\/api$/, '') || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

const PRESET_FLOW = {
  name: 'Appointment Booking Bot',
  description: 'Guides user to book appointment',
  triggerKeywords: ['book', 'appointment', 'schedule'],
  startStepId: 's1',
  steps: [
    { id: 's1', type: 'message', text: 'Namaste! I can help you book an appointment. Let me ask a few questions.', nextStepId: 's2' },
    { id: 's2', type: 'question', text: 'What is your full name?', variable: 'name', nextStepId: 's3' },
    { id: 's3', type: 'question', text: 'Preferred date? (e.g. 25th March)', variable: 'date', nextStepId: 's4' },
    { id: 's4', type: 'question', text: 'Morning or Evening slot?', variable: 'slot', options: [
      { label: 'Morning (10am-1pm)', value: 'morning', nextStepId: 's5' },
      { label: 'Evening (5pm-9pm)', value: 'evening', nextStepId: 's5' }
    ] },
    { id: 's5', type: 'message', text: 'Thanks! Our team will confirm your booking within 30 minutes.', nextStepId: 's6' },
    { id: 's6', type: 'handoff', text: '(Human agent will take over)' }
  ]
};

export default function WhatsAppFlowsPage() {
  const [flows, setFlows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` };

  const load = async () => {
    try { const { data } = await axios.get(`${API}/api/saas/wa-flows`, { headers }); setFlows(data); }
    catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);

  const createFromPreset = async () => {
    try { await axios.post(`${API}/api/saas/wa-flows`, PRESET_FLOW, { headers }); load(); }
    catch { alert('Failed'); }
  };

  const save = async () => {
    try {
      if (editing._id) await axios.patch(`${API}/api/saas/wa-flows/${editing._id}`, editing, { headers });
      else await axios.post(`${API}/api/saas/wa-flows`, editing, { headers });
      setEditing(null); load();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete flow?')) return;
    await axios.delete(`${API}/api/saas/wa-flows/${id}`, { headers });
    load();
  };

  const simulate = async (id) => {
    const { data } = await axios.post(`${API}/api/saas/wa-flows/${id}/simulate`, {}, { headers });
    setSimulation(data.trace);
  };

  const addStep = () => {
    const id = `s${editing.steps.length + 1}`;
    setEditing({ ...editing, steps: [...editing.steps, { id, type: 'message', text: '', nextStepId: '' }] });
  };

  const updateStep = (i, patch) => {
    const newSteps = [...editing.steps];
    newSteps[i] = { ...newSteps[i], ...patch };
    setEditing({ ...editing, steps: newSteps });
  };

  const removeStep = (i) => {
    setEditing({ ...editing, steps: editing.steps.filter((_, idx) => idx !== i) });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>🤖 Chatbot Flows</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={createFromPreset} style={btnSecondary}>📋 Create from Preset</button>
          <button onClick={() => setEditing({ name: '', description: '', triggerKeywords: [], startStepId: 's1', steps: [{ id: 's1', type: 'message', text: '', nextStepId: '' }] })} style={btnPrimary}>+ New Flow</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {flows.map(f => (
          <div key={f._id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <strong>{f.name}</strong>
              <span style={{ fontSize: 11, color: f.enabled ? '#10b981' : '#888' }}>{f.enabled ? '● live' : '○ off'}</span>
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{f.description || 'No description'}</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              Triggers: {(f.triggerKeywords || []).join(', ') || '(none)'}
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
              {f.steps.length} steps · {f.runCount} runs
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setEditing(f)} style={miniBtn}>Edit</button>
              <button onClick={() => simulate(f._id)} style={miniBtn}>▶ Simulate</button>
              <button onClick={() => remove(f._id)} style={{ ...miniBtn, color: '#ef4444' }}>Delete</button>
            </div>
          </div>
        ))}
        {flows.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#888', padding: 40 }}>No flows yet</div>}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing._id ? 'Edit Flow' : 'New Flow'}>
          <input placeholder="Flow name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} style={input} />
          <input placeholder="Description" value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} style={input} />
          <input placeholder="Trigger keywords (comma separated)" value={(editing.triggerKeywords || []).join(', ')} onChange={e => setEditing({ ...editing, triggerKeywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean) })} style={input} />
          <input placeholder="Start step ID" value={editing.startStepId} onChange={e => setEditing({ ...editing, startStepId: e.target.value })} style={input} />

          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <strong>Steps</strong>
            <button onClick={addStep} style={{ ...miniBtn, marginLeft: 8 }}>+ Add Step</button>
          </div>
          {editing.steps.map((s, i) => (
            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input placeholder="ID" value={s.id} onChange={e => updateStep(i, { id: e.target.value })} style={{ ...input, width: 60, marginBottom: 0 }} />
                <select value={s.type} onChange={e => updateStep(i, { type: e.target.value })} style={{ ...input, width: 120, marginBottom: 0 }}>
                  <option value="message">Message</option>
                  <option value="question">Question</option>
                  <option value="condition">Condition</option>
                  <option value="handoff">Handoff</option>
                  <option value="end">End</option>
                </select>
                <input placeholder="Next step ID" value={s.nextStepId || ''} onChange={e => updateStep(i, { nextStepId: e.target.value })} style={{ ...input, flex: 1, marginBottom: 0 }} />
                <button onClick={() => removeStep(i)} style={{ ...miniBtn, color: '#ef4444' }}>×</button>
              </div>
              <textarea placeholder="Text shown to user" value={s.text || ''} onChange={e => updateStep(i, { text: e.target.value })} style={{ ...input, minHeight: 50 }} />
              {s.type === 'question' && (
                <input placeholder="Variable name (to store answer)" value={s.variable || ''} onChange={e => updateStep(i, { variable: e.target.value })} style={input} />
              )}
            </div>
          ))}
          <button onClick={save} style={btnPrimary}>Save Flow</button>
        </Modal>
      )}

      {simulation && (
        <Modal onClose={() => setSimulation(null)} title="Simulation Trace">
          {simulation.map((t, i) => (
            <div key={i} style={{ padding: 10, marginBottom: 6, background: '#f9fafb', borderRadius: 6, borderLeft: '3px solid #25D366' }}>
              <div style={{ fontSize: 11, color: '#888' }}>{t.id} · {t.type}</div>
              <div style={{ fontSize: 14 }}>{t.text || t.error}</div>
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const input = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const btnSecondary = { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' };
const miniBtn = { padding: '4px 10px', fontSize: 12, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' };
const card = { background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
